import { VelocityActions } from '../components/VelocityActions';
import { ServerCardFrame } from '../components/ServerCardFrame';
import { velocityApi } from '../api/velocity';
import { LoadingContent, useAnimatedList, Spinner } from '../components/feedback';
import { EdgeIpWarning } from '../components/EdgeIpWarning';
import { Link } from 'react-router';
import { useEffect, useState, type DragEvent } from 'react';
import { errorMessage, useApp } from '../app-context';
import { api } from '../api/client';
import { api3 } from '../api/client-p3';
import { EdgeWarning } from '../components/EdgeWarning';
import { NodeDisks } from '../components/NodeDisks';
import { NodeTrends } from '../components/NodeTrends';
import { NodeVms } from '../components/NodeVms';
import type { NodeStatus, ServerView } from '../api/types';
import { ServerActions } from '../components/ServerActions';
import { Badge, Empty, ErrorAlert, Meter, Stat, StateBadge } from '../components/ui';
import { formatGb, percent, softwareLabel, tpsTone } from '../format';
import { usePoll } from '../hooks/usePoll';
import { applyNodeOrder, moveName, readNodeOrder, saveNodeOrder } from '../node-order';

type NodeReorder = {
  index: number;
  count: number;
  move: (from: number, to: number) => void;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
};

function NodeCard({ node, reorder }: { node: NodeStatus; reorder?: NodeReorder }) {
  const { me, ensureSteppedUp, notify } = useApp();
  const [busy, setBusy] = useState(false);
  // 止まっている VM はメモリを使わないので、棒と色は動いている VM の割り当てだけで出す
  const runningMb = node.memoryRunningMb ?? node.memoryAllocatedMb;
  const stoppedMb = Math.max(0, node.memoryAllocatedMb - runningMb);
  const wake = async () => {
    setBusy(true);
    try {
      await ensureSteppedUp();
      await api.wakeNode(node.name);
      notify('起動信号を送信しました。オンラインになるまでお待ちください。');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const from = Number(event.dataTransfer.getData('text/plain'));
    if (reorder && Number.isInteger(from)) reorder.move(from, reorder.index);
  };
  return (
    <div
      className={`card card-body stack${reorder ? ' node-card-reorder' : ''}${reorder?.dragging ? ' dragging' : ''}`}
      style={{ gap: 10 }}
      draggable={!!reorder}
      onDragStart={
        reorder
          ? (event) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', String(reorder.index));
              reorder.onDragStart();
            }
          : undefined
      }
      onDragEnd={reorder?.onDragEnd}
      onDragOver={reorder ? (event) => event.preventDefault() : undefined}
      onDrop={reorder ? drop : undefined}
    >
      {reorder && (
        <div className="row node-reorder-bar">
          <span className="small muted" aria-hidden="true">
            ⠿ 移動
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="btn small"
            disabled={reorder.index === 0}
            onClick={() => reorder.move(reorder.index, reorder.index - 1)}
            aria-label={`${node.name} を前へ`}
          >
            ←
          </button>
          <button
            type="button"
            className="btn small"
            disabled={reorder.index === reorder.count - 1}
            onClick={() => reorder.move(reorder.index, reorder.index + 1)}
            aria-label={`${node.name} を後ろへ`}
          >
            →
          </button>
        </div>
      )}
      <div className="row">
        <h2>{node.name}</h2>
        <Badge tone="muted">
          {
            { world: 'サーバー用', 'world-light': '軽いサーバー用', infra: '基盤用', spare: '予備' }[
              node.role ?? 'world'
            ]
          }
        </Badge>
        <span className="spacer" />
        <Badge tone={node.online ? 'ok' : 'danger'}>{node.online ? 'オンライン' : 'オフライン'}</Badge>
      </div>
      {!node.online && node.role === 'spare' && me?.user?.role === 'owner' && (
        <button className="btn" disabled={busy} onClick={() => void wake()}>
          {busy && <Spinner />}
          起動する
        </button>
      )}
      <Meter label="CPU" value={node.cpu} max={1} detail={percent(node.cpu)} />
      <Meter
        label="メモリ割り当て（動いている VM）"
        value={runningMb}
        max={node.memoryTotalMb}
        detail={`${formatGb(runningMb)} / ${formatGb(node.memoryTotalMb)}`}
      />
      {(node.online || stoppedMb > 0) && (
        <p className="small muted" style={{ marginTop: -6 }}>
          {node.online && <>実際に使っているメモリ: {formatGb(node.memoryUsedMb)}</>}
          {node.online && stoppedMb > 0 && <br />}
          {stoppedMb > 0 && (
            <>
              止まっている VM の割り当て: {formatGb(stoppedMb)}（起動すると使う。新しいサーバーの空きの計算には含める）
            </>
          )}
        </p>
      )}
      {node.disks && <NodeDisks disks={node.disks} />}
      {node.vms && node.vms.length > 0 && <NodeVms node={{ ...node, vms: node.vms }} />}
    </div>
  );
}

function ServerCard({
  server,
  onChange,
  className,
  exiting,
  itemRef,
}: {
  server: ServerView;
  onChange: () => void;
  className: string;
  exiting: boolean;
  itemRef: (element: HTMLElement | null) => void;
}) {
  const live = server.live;
  const running = server.state === 'running';
  return (
    <ServerCardFrame
      name={server.name}
      href={`/servers/${encodeURIComponent(server.id)}`}
      state={<StateBadge state={server.state} unreachable={!!server.unreachable} />}
      className={className}
      exiting={exiting}
      itemRef={itemRef}
    >
      <div className="row small muted">
        <span className="tag">
          {softwareLabel[server.software.family]} {server.software.mcVersion}
        </span>
        {server.node && <span>{server.node}</span>}
        {server.proxied && <span className="tag">Velocity 配下</span>}
        {server.vm?.stopping ? (
          <span className="tag">VM シャットダウン中</span>
        ) : (
          server.vm?.running === false && <span className="tag">VM 停止済み</span>
        )}
      </div>
      <div className="stats">
        <Stat
          label="プレイヤー"
          value={running && live ? live.players : '—'}
          unit={running && live ? `/ ${live.maxPlayers}` : undefined}
        />
        <Stat
          label="TPS"
          value={
            running && live?.tps !== undefined ? (
              <span style={{ color: `var(--${tpsTone(live.tps)})` }}>{live.tps.toFixed(1)}</span>
            ) : (
              '—'
            )
          }
        />
        <Stat
          label="MSPT"
          value={running && live?.mspt !== undefined ? live.mspt.toFixed(1) : '—'}
          unit={running && live?.mspt !== undefined ? 'ms' : undefined}
        />
      </div>
      {running && live?.memoryUsedMb !== undefined && (
        <Meter
          label="メモリ"
          neutral
          value={live.memoryUsedMb}
          max={server.resources.memoryMb}
          detail={`${formatGb(live.memoryUsedMb)} / ${formatGb(server.resources.memoryMb)}`}
        />
      )}
      <div className="server-card-actions">
        <ServerActions server={server} onDone={onChange} compact />
      </div>
    </ServerCardFrame>
  );
}

export function Dashboard() {
  const { canOperate, me, notify } = useApp();
  const userId = me?.user?.id;
  const [nodeOrder, setNodeOrder] = useState(() => readNodeOrder(userId));
  const [reordering, setReordering] = useState(false);
  const [dragging, setDragging] = useState<string>();
  useEffect(() => setNodeOrder(readNodeOrder(userId)), [userId]);
  const velocity = usePoll(() => velocityApi.get(), 10000);
  const servers = usePoll(() => api.servers(), 5000);
  const routing = usePoll(() => api3.routing(), 15000);
  const nodes = usePoll(() => api.nodes(), 10000);
  const serverList = useAnimatedList(servers.data ?? [], (server) => server.id);
  const orderedNodes = applyNodeOrder(nodes.data ?? [], nodeOrder);
  const nodeList = useAnimatedList(orderedNodes, (node) => node.name);
  const changeNodeOrder = (order: string[] | undefined) => {
    setNodeOrder(order);
    if (!saveNodeOrder(userId, order)) notify('並びを保存できませんでした。再読み込みすると元に戻ります。', true);
  };
  const moveNode = (from: number, to: number) =>
    changeNodeOrder(
      moveName(
        orderedNodes.map((node) => node.name),
        from,
        to,
      ),
    );
  const totalPlayers = (servers.data ?? []).reduce(
    (sum, s) => sum + (s.state === 'running' ? (s.live?.players ?? 0) : 0),
    0,
  );
  const running = (servers.data ?? []).filter((s) => s.state === 'running').length;

  return (
    <div className="stack" style={{ gap: 24 }}>
      <EdgeIpWarning edge={routing.data?.edge} />
      <div className="page-header">
        <div>
          <h1 data-search-id="dashboard">ダッシュボード</h1>
          <p>
            {servers.data
              ? `${servers.data.length} サーバー中 ${running} 稼働 ・ 接続中 ${totalPlayers} 人`
              : '読み込み中…'}
          </p>
        </div>
        {canOperate && (
          <Link to="/servers/new" className="btn primary">
            サーバーを作成
          </Link>
        )}
      </div>

      <EdgeWarning routing={routing.data} link />
      <p className="small muted">
        プレイヤーにランチャーを配るには、<a href="/launcher">配布ページ</a>の URL を共有してください。
      </p>
      <section className="stack" style={{ gap: 10 }}>
        <h3>サーバー</h3>
        <ErrorAlert error={servers.error} />
        {servers.data && servers.data.length === 0 && <Empty>サーバーはまだありません。</Empty>}
        <LoadingContent loading={!servers.data && servers.loading} shape="cards">
          <div className="grid grid-servers">
            <ServerCardFrame
              name="Velocity（入口）"
              href="/velocity"
              state={<StateBadge state={velocity.data?.state ?? 'unknown'} />}
            >
              <div className="row small muted">
                Velocity {velocity.data?.version ?? '—'}
                <span>edge VM 101</span>
              </div>
              <div className="stats">
                <Stat
                  label="プレイヤー"
                  value={velocity.data?.network?.online ?? '—'}
                  unit={velocity.data?.network ? '/ ' + velocity.data.network.max : undefined}
                />
                <Stat label="プラグイン" value={velocity.data?.plugins.length ?? '—'} />
              </div>
              {velocity.data?.message && <p className="small muted">{velocity.data.message}</p>}
              {velocity.data && (
                <div className="server-card-actions">
                  <VelocityActions data={velocity.data} reload={() => void velocity.reload()} compact />
                </div>
              )}
            </ServerCardFrame>
            {serverList.items.map((server) => (
              <ServerCard
                key={server.id}
                server={server}
                onChange={() => void servers.reload()}
                className={serverList.className(server)}
                exiting={!servers.data?.some((item) => item.id === server.id)}
                itemRef={serverList.ref}
              />
            ))}
          </div>
        </LoadingContent>
      </section>

      <section className="stack" style={{ gap: 10 }}>
        <div className="row">
          <h3>ノード</h3>
          <span className="spacer" />
          {reordering && nodeOrder && (
            <button type="button" className="btn small ghost" onClick={() => changeNodeOrder(undefined)}>
              名前順に戻す
            </button>
          )}
          {(nodes.data?.length ?? 0) > 1 && (
            <button
              type="button"
              className={`btn small${reordering ? ' primary' : ''}`}
              aria-pressed={reordering}
              onClick={() => setReordering(!reordering)}
            >
              {reordering ? '完了' : '並び替え'}
            </button>
          )}
        </div>
        {reordering && (
          <p className="small muted">
            カードをドラッグするか ← → で並びを変えられます。並びはこのブラウザに、あなたの分だけ保存されます。
          </p>
        )}
        <ErrorAlert error={nodes.error} />
        <LoadingContent loading={!nodes.data && nodes.loading} shape="cards">
          <div className="grid grid-nodes">
            {nodeList.items.map((node) => {
              // 消えかけのカード（一覧から外れたノード）は並び替えの対象にしない
              const index = orderedNodes.findIndex((item) => item.name === node.name);
              return (
                <NodeCard
                  key={node.name}
                  node={node}
                  reorder={
                    reordering && index >= 0
                      ? {
                          index,
                          count: orderedNodes.length,
                          move: moveNode,
                          dragging: dragging === node.name,
                          onDragStart: () => setDragging(node.name),
                          onDragEnd: () => setDragging(undefined),
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        </LoadingContent>
      </section>

      {nodes.data && <NodeTrends nodes={orderedNodes} />}
    </div>
  );
}
